import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { ArrowRight, Zap, Star } from 'lucide-react';
import logoImage from '../data/d.png';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';

const SpaceHero = () => {
  const { t } = useTranslation();
  const { language } = useLanguage ? useLanguage() : { language: 'en' };
  
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
  const containerRef = useRef(null);
  
  // Parallax transforms for local layers
  const { scrollY } = useScroll();
  const contentY = useTransform(scrollY, [0, 500], [0, -80]);
  const logoScale = useTransform(scrollY, [0, 500], [1, 0.9]);
  const logoRotate = useTransform(scrollY, [0, 500], [0, 25]);
  const contentOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  const smoothContentY = useSpring(contentY, { stiffness: 100, damping: 30 });
  const smoothLogoScale = useSpring(logoScale, { stiffness: 100, damping: 30 });
  const smoothLogoRotate = useSpring(logoRotate, { stiffness: 100, damping: 30 });
  const smoothContentOpacity = useSpring(contentOpacity, { stiffness: 100, damping: 30 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setMouse({
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section 
      ref={containerRef}
      className="relative h-[calc(100vh-80px)] min-h-[620px] max-h-[900px] flex items-center justify-center bg-transparent overflow-hidden px-4 sm:px-6 lg:px-8 select-none"
      style={{ perspective: "1500px" }}
    >
      {/* Local mouse glow mesh */}
      <motion.div
        className="absolute w-[20rem] h-[20rem] bg-[radial-gradient(circle,rgba(0,255,136,0.08)_0%,transparent_70%)] rounded-full pointer-events-none mix-blend-screen"
        style={{
          left: `${mouse.x * 100}%`,
          top: `${mouse.y * 100}%`,
          translateX: "-50%",
          translateY: "-50%",
        }}
      />

      {/* Main Container: Elegant side-by-side layout on larger screens, compact stacked layout on mobile */}
      <div className="container mx-auto max-w-7xl h-full flex items-center relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center w-full">
          
          <motion.div
            style={{ 
              opacity: smoothContentOpacity, 
              y: smoothContentY,
              transformStyle: "preserve-3d" 
            }}
            initial={{ opacity: 0, x: language === 'ar' ? 80 : -80 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="lg:col-span-7 text-center lg:text-left rtl:lg:text-right flex flex-col justify-center"
          >
            {/* Main Title Heading */}
            <h1 className={`font-black text-white mb-6 tracking-tight leading-none drop-shadow-2xl ${
              language === 'ar' 
                ? 'text-4xl sm:text-5xl lg:text-6xl leading-snug font-cairo' 
                : 'text-4xl sm:text-5xl lg:text-7xl'
            }`}>
              <span className="gradient-text-neon">{t('hero.title')}</span>
            </h1>

            {/* Subtitle Description */}
            <p className={`font-light mb-8 text-white/80 leading-relaxed drop-shadow-md max-w-2xl mx-auto lg:mx-0 ${
              language === 'ar' 
                ? 'text-base sm:text-lg lg:text-xl' 
                : 'text-base sm:text-lg lg:text-2xl'
            }`}>
              {t('hero.subtitle')}
            </p>

            {/* 3D Call to Action Trigger Button */}
            <div className="flex justify-center lg:justify-start">
              <motion.a
                href="#about"
                whileHover={{ scale: 1.05, y: -4, boxShadow: "0 15px 30px rgba(0, 255, 136, 0.35)" }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center space-x-3 rtl:space-x-reverse bg-gradient-to-r from-neon-green to-neon-cyan text-black px-8 py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition-all duration-300 shadow-[0_4px_20px_rgba(0,255,136,0.15)] cursor-pointer"
                style={{ transform: "translateZ(30px)" }}
              >
                <span>{t('hero.cta')}</span>
                <ArrowRight className="w-5 h-5 animate-pulse" />
              </motion.a>
            </div>
          </motion.div>

          {/* Column 2: 3D Orbiting Interactive Logo */}
          <motion.div
            style={{ 
              y: smoothContentY, 
              scale: smoothLogoScale, 
              rotate: smoothLogoRotate,
              transformStyle: "preserve-3d"
            }}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.5, delay: 0.2, ease: "easeOut" }}
            className="lg:col-span-5 flex justify-center items-center select-none"
          >
            <div className="relative flex justify-center items-center">
              {/* Neon Glow Ring */}
              <div className="absolute w-[18rem] h-[18rem] sm:w-[22rem] sm:h-[22rem] bg-gradient-to-r from-neon-green/30 via-neon-cyan/15 to-neon-purple/30 rounded-full blur-3xl animate-pulse-slow"></div>
              
              {/* Floating Glassmorphic Plate */}
              <motion.div
                className="relative w-[14rem] h-[14rem] sm:w-[17rem] sm:h-[17rem] bg-gradient-to-br from-white/95 via-white/90 to-white/95 rounded-full border-4 border-neon-green/30 flex items-center justify-center shadow-[0_0_40px_rgba(0,255,136,0.2)]"
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 0 50px rgba(0, 255, 136, 0.4)",
                  translateZ: 50
                }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                <img 
                  src={logoImage} 
                  alt="Saham Holding Logo"
                  className="w-[10rem] h-[10rem] sm:w-[13rem] sm:h-[13rem] object-contain drop-shadow-[0_10px_10px_rgba(0,0,0,0.15)]"
                  style={{ transform: "translateZ(40px)" }}
                />
                
                {/* Embedded Ambient Inner Light */}
                <div className="absolute inset-3 rounded-full bg-gradient-to-br from-neon-green/10 to-neon-cyan/10 animate-pulse-slow"></div>
              </motion.div>

              {/* Interactive Orbiting Planetoids */}
              <motion.div
                className="absolute w-[18rem] h-[18rem] sm:w-[21rem] sm:h-[21rem] pointer-events-none"
                animate={{ rotate: 360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              >
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-3.5 h-3.5 bg-neon-green rounded-full shadow-[0_0_12px_#00ff88] animate-pulse"></div>
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-neon-cyan rounded-full shadow-[0_0_12px_#00d4ff]"></div>
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-2.5 h-2.5 bg-neon-blue rounded-full shadow-[0_0_12px_#0088ff]"></div>
                <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 bg-neon-purple rounded-full shadow-[0_0_12px_#a855f7] animate-pulse"></div>
              </motion.div>
            </div>
          </motion.div>

        </div>
      </div>

      {/* Smooth Scroll down Indicator */}
      <motion.div
        style={{ opacity: smoothContentOpacity }}
        className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20 flex flex-col items-center"
      >
        <span className="text-white/40 text-[10px] font-semibold uppercase tracking-widest mb-1.5 animate-pulse">{t('hero.scrollText') || 'Scroll down'}</span>
        <div className="w-5 h-8 border-2 border-white/20 rounded-full flex justify-center">
          <motion.div
            className="w-0.5 h-2 bg-neon-green rounded-full mt-1.5"
            animate={{ y: [0, 8, 0], opacity: [1, 0.4, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default SpaceHero;